<template>
    <div class="hotel-price">
        <Divider color="lightGrey" topMargin="l" bottom-margin="l" />
        <div class="hotel-price-content">
            <ButtonLink
                size="m"
                color="black"
                class="hotel-price-content-link"
                :onClick="onPriceDetailsClick">
                {{ t({ key: 'shared.price_details' }) }}
            </ButtonLink>
            <div class="hotel-price-content-info">
                <ProductPricing v-bind="productPricing" contentPosition="end" />
                <span class="hotel-price-content-info-onsite">{{ payOnSite }}</span>
            </div>
        </div>
        <Divider color="lightGrey" topMargin="l" bottom-margin="l" />
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, PropType } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { useStore } from '@/store';
    import sumBy from 'lodash/sumBy';
    import { ProductModalTab, ModalType } from '@/store/general/types';
    import { HotelProduct } from '@/api/Cart/cart.interfaces';
    import {
        getMoneyAsStringUsingCurrency,
        getMoneyObjectAsString,
    } from '@shared/helpers/money';
    import { Money } from '@libs/money/money.interfaces';
    import { LabelFormat } from '@shared/interfaces/translation';
    import { getProductPricing } from '@/helpers/price';
    import { logStatsigCheckoutPriceDetailsOpen } from '@/tracking/statsig/checkout.functions';
    import ButtonLink from '@ui/components/Button/ButtonLink.vue';
    import ProductPricing from '@ui/components/ProductPricing/ProductPricing.vue';
    import Divider from '@ui/components/Divider/Divider.vue';

    export default defineComponent({
        name: 'HotelPrice',
        methods: { getMoneyObjectAsString, t },
        components: { Divider, ProductPricing, ButtonLink },
        props: {
            product: {
                type: Object as PropType<HotelProduct>,
                required: true,
            },
            showAmount: { type: Boolean, default: true },
        },
        setup(props) {
            const store = useStore();

            const productPricing = computed(() => getProductPricing(props.product));

            const payOnSite = computed(() => {
                const { Rooms } = props.product;
                const total: Money = {
                    Amount: sumBy(Rooms, (room) => room.PriceAtLocation?.Amount || 0),
                    Currency: Rooms[0]?.PriceAtLocation?.Currency ?? '',
                };

                return total.Amount > 0
                    ? t({
                          prefix: '(',
                          key: 'checkout.pay_on_site',
                          values: [
                              getMoneyAsStringUsingCurrency(total.Amount, total.Currency),
                          ],
                          suffix: ')',
                          format: LabelFormat.LowerCase,
                      })
                    : undefined;
            });

            function onPriceDetailsClick() {
                store.dispatch('general/setModal', {
                    type: ModalType.Product,
                    productId: props.product.Id,
                    activeTab: ProductModalTab.Price,
                });

                logStatsigCheckoutPriceDetailsOpen(props.product.Type);
            }

            return {
                productPricing,
                payOnSite,
                onPriceDetailsClick,
            };
        },
    });
</script>

<style scoped lang="scss">
    @forward './Price.scss';
</style>
